import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { ReactComponent as NoteIndicationAttachedOrInProgress } from "assets/svg/note-indication-attached-or-in-progress.svg";
import NoteTemplateSelector from "components/Dashboard/DoctorDashboard/components/note-template-selector/NoteTemplateSelector";
import { ADD_NOTE_IN_CALENDAR_REDIRECTS_TO_SESSION_REVIEW } from "constants/featureFlags";
import useToggleModal from "hooks/useToggleModal";
import messages from "messages";
import { noteDataInfoInfiniteQueryOptions } from "queries/noteDataInfoQueryOptions";
import { noteMetaDataMinimalsInfiniteQueryOptions } from "queries/noteMetaDataMinimalsQueryOptions";
import useFetchAllPagesForInfiniteQuery from "queries/useFetchAllPagesForInfiniteQuery";
import { useCallback, useMemo } from "react";
import { Button } from "react-bootstrap";
import attachNoteToAppointment from "services/scheduler/attachNoteToAppointment";
import getClasses from "utils/getClasses";
import styles from "../../SessionSidesheet.module.scss";
import NoteListItem from "../note-list-item/NoteListItem";

interface NotesSessionDetailsSectionProps {
  appointment: AppointmentWithSeriesInfo;
  trackClick: (elementName: string) => void;
}

const getLatestNoteRevisions = (allNotesRevisions: NoteDataInfo[]) => {
  const latestNotesRevisionsMap = allNotesRevisions.reduce((acc, note) => {
    if (!acc[note.note_data_id]) {
      acc[note.note_data_id] = note;
    } else if (acc[note.note_data_id].note_revision < note.note_revision) {
      acc[note.note_data_id] = note;
    }

    return acc;
  }, {} as Record<string, NoteDataInfo>);

  const latestNotesRevisions = Object.values(latestNotesRevisionsMap);
  return latestNotesRevisions;
};

const NotesSessionDetailsSection = ({
  appointment,
  trackClick,
}: NotesSessionDetailsSectionProps) => {
  const queryClient = useQueryClient();

  const noteMetaDataMinimalsQuery = useInfiniteQuery(
    noteMetaDataMinimalsInfiniteQueryOptions({
      appointmentId: appointment.appointment_id,
    }),
  );
  useFetchAllPagesForInfiniteQuery(noteMetaDataMinimalsQuery);

  const noteIds = useMemo(
    () =>
      noteMetaDataMinimalsQuery.data?.pages
        .flat()
        .map((note) => note.note_data_id) ?? [],
    [noteMetaDataMinimalsQuery.data],
  );

  const noteDataInfoQuery = useInfiniteQuery({
    ...noteDataInfoInfiniteQueryOptions({
      noteDataId: noteIds,
    }),
    enabled: noteIds.length > 0,
  });
  useFetchAllPagesForInfiniteQuery(noteDataInfoQuery);

  const sessionNotes = useMemo(
    () => getLatestNoteRevisions(noteDataInfoQuery.data?.pages.flat() ?? []),
    [noteDataInfoQuery.data],
  );

  const isLoadingSessionNotes = useMemo(
    () =>
      [noteMetaDataMinimalsQuery, noteDataInfoQuery].some(
        (query) => query.isLoading,
      ),
    [noteMetaDataMinimalsQuery, noteDataInfoQuery],
  );

  const reloadSessionNotes = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: ["noteDataInfo", { noteDataId: noteIds }],
    });
    await queryClient.invalidateQueries({
      queryKey: [
        "noteMetaDataMinimals",
        {
          appointmentId: appointment.appointment_id,
        },
      ],
    });
  }, [noteIds, appointment.appointment_id, queryClient]);

  const isCancelled = appointment.statusFlags.cancelledOrAbandoned;
  const hasRecordingInProgress =
    !appointment.session_recording_upload_metadata?.finished &&
    appointment.session_recording_upload_metadata?.uploadId;
  const isDisabled =
    isCancelled || isLoadingSessionNotes || !!hasRecordingInProgress;

  const {
    show: showNoteTemplateSelector,
    open: openNoteTemplateSelector,
    close: closeNoteTemplateSelector,
  } = useToggleModal(false);

  const handleAddProgressNodeClick = useCallback(() => {
    trackClick("add-progress-note-button");
    if (ADD_NOTE_IN_CALENDAR_REDIRECTS_TO_SESSION_REVIEW) {
      return;
    }
    openNoteTemplateSelector();
  }, [trackClick, openNoteTemplateSelector]);

  const handleNoteTemplateSelectorApply = useCallback(
    async (noteSchema: NoteSchema) => {
      await attachNoteToAppointment({
        appointmentId: appointment.appointment_id,
        patientId: appointment.patient.cognito_sub,
        noteSchema,
      });

      await reloadSessionNotes();
    },
    [reloadSessionNotes, appointment],
  );

  return (
    <div className={styles["section"]}>
      <div className={styles["section__title"]}>{messages.notes}</div>
      {sessionNotes.length === 0 && (
        <div className={styles["text-with-icon"]}>
          <NoteIndicationAttachedOrInProgress
            className={getClasses(isDisabled && styles["disabled-icon"])}
          />
          {ADD_NOTE_IN_CALENDAR_REDIRECTS_TO_SESSION_REVIEW && !isDisabled ? (
            <a
              href={`/pro-dashboard/session-review/${appointment.appointment_id}`}
              style={{ textDecoration: "none" }}
            >
              {messages.addProgressNote}
            </a>
          ) : (
            <Button
              disabled={isDisabled}
              variant={"link"}
              className={"p-0"}
              onClick={handleAddProgressNodeClick}
            >
              {messages.addProgressNote}
            </Button>
          )}
        </div>
      )}
      {sessionNotes.map((note) => (
        <NoteListItem
          key={note.note_data_id}
          note={note}
          trackClick={trackClick}
          isSessionCancelled={isCancelled}
          appointmentId={appointment.appointment_id}
        />
      ))}

      <NoteTemplateSelector
        requireConfirmation={true}
        show={showNoteTemplateSelector}
        onHide={closeNoteTemplateSelector}
        onApply={handleNoteTemplateSelectorApply}
        labelModalHeader={messages.attachNote}
        labelInfo={messages.chooseANoteTemplate}
        labelApply={messages.attach}
      />
    </div>
  );
};

export default NotesSessionDetailsSection;
